'use client'

import { useState } from 'react'
import Link from 'next/link'

import styled from 'styled-components'

import { ReviewItem } from '../../../features/marketItem/ui/components/ReviewItem'
import { MarketItemRates } from '../../../features/marketItem/ui/components/MarketItemRates'
import {
  GetProductInfoResponse,
  GetReviewsListResponse,
  GetUserProfileResponse,
} from '../../../shared/api/generated'
import { colors } from '../../../shared/constants/colors'
import { Paragraph } from '../../../shared/ui-kit/actual/Paragraph'
import { ProductCharacteristics } from '../../../shared/ui-kit/actual/ProductCharacteristics'
import { ExistedPrice } from '../../../shared/ui-kit/actual/components/ExistedPrice'
import { LineBreaks } from '../../../shared/ui-kit/actual/components/LineBreaks'
import { ButtonColumn } from '../../../shared/ui-kit/actual/components/buttonColumn'
import { breakpoints, devices } from '../../../shared/ui-kit/breakPoints'
import {
  DesktopView,
  FlexColumn,
  FlexColumnBetween,
  FlexColumnStart,
  FlexRow,
  FlexRowBetween,
  MediumTextL,
  MobileTabletView,
  StyledButton,
} from '../../../shared/ui-kit/projectComponents'
import displayStringByAmount, {
  displayRateByAmount,
} from '../../../shared/utils/displayStringByAmount'
import { getDateAgo } from '../../../shared/utils/dateAgo'
import getAvgRating, { getProductAvgRating } from '../../../shared/utils/getAvgRating'

import { ItemBottomMenuClient } from './ItemBottomMenuClient'
import { ItemDesktopHeaderClient } from './ItemDesktopHeaderClient'
import { ItemGalleryClient } from './ItemGalleryClient'
import { ItemLocationClient } from './ItemLocationClient'
import { ItemMobileHeaderClient } from './ItemMobileHeaderClient'
import { ItemOrderClient } from './ItemOrderClient'

const Page = styled(FlexColumn)`
  width: clamp(${breakpoints.mobileMin}, 100%, ${breakpoints.desktopMiddle});
  margin: 0 auto;
  flex-direction: column;
  box-sizing: border-box;
  padding: 0 24px;
`

const BlockTitle = styled.h2<{
  bottomSpace?: 'md' | 'sm' | 'xl' | 'zero'
  topSpace?: 'md' | 'sm' | 'xl' | 'zero'
}>`
  text-align: left;
  margin: 0;
  margin-bottom: ${({ bottomSpace }) =>
    (bottomSpace === 'xl' && '32px') ||
    (bottomSpace === 'md' && '24px') ||
    (bottomSpace === 'sm' && '16px') ||
    '0'};
  margin-top: ${({ topSpace }) =>
    (topSpace === 'xl' && '32px') ||
    (topSpace === 'md' && '24px') ||
    (topSpace === 'sm' && '16px') ||
    '0'};
`

const Wrapper = styled(FlexColumn)`
  width: clamp(${breakpoints.mobileMin}, 100%, ${breakpoints.desktopMin});
  flex-direction: column;
  box-sizing: border-box;
  padding: 0;
  padding-bottom: 36px;
`

const SubTitle = styled.h6`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #008c8c;
`

const Box = styled.div`
  display: flex;
  width: 100%;
  justify-content: start;
`

const TableWrapper = styled.div`
  width: -webkit-fill-available;
  padding: 18px 20px;
  background-color: #f8f8fa;
  border-radius: 10px;
`

const BtnWrapper = styled(FlexColumn)`
  width: 100%;
  margin: 24px 0 8px;
  gap: 12px;
`

const BottomButton = styled(StyledButton)`
  margin-bottom: 96px;
  margin-top: 12px;
  @media screen and (${devices.desktopMin}) {
    margin-bottom: 24px;
  }
`

const OwnerLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  width: 100%;
`

const ReviewsWrapper = styled(FlexColumnStart)`
  row-gap: 24px;
  width: 100%;
`

const ReviewsLinkButton = styled.button`
  background: transparent;
  border: 0;
  cursor: pointer;
  padding: 0;
`

const LocationSubTitle = styled.p`
  margin: 0;
  padding: 0;
  line-height: 23.36px;
  color: ${colors.black};
  font-size: 16px;
`

const LocationWrapper = styled(FlexRowBetween)`
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  @media screen and (${devices.tabletMax}) {
    flex-wrap: nowrap;
  }
`

interface ItemServerViewProps {
  productInfo: GetProductInfoResponse
  productReviews?: GetReviewsListResponse
  sellerInfo?: GetUserProfileResponse
}

export function ItemServerView({
  productInfo,
  productReviews = { items: [], totalCount: 0 },
  sellerInfo,
}: ItemServerViewProps) {
  const [openRating, setOpenRating] = useState(false)
  const title = productInfo.name || 'Товар'
  const avgRating = getProductAvgRating(productReviews)
  const avgSellerRating = getAvgRating(sellerInfo?.buyerRating || 0, sellerInfo?.sellerRating || 0)
  const reviews = [...(productReviews.items || [])].reverse().slice(0, 3)

  if (openRating) {
    return (
      <FlexColumn style={{ width: '100%' }}>
        <MarketItemRates
          avgRate={productInfo.rating || 0}
          itemData={productReviews.items || []}
          setOpenRating={setOpenRating}
        />
        <ItemBottomMenuClient />
      </FlexColumn>
    )
  }

  return (
    <>
      <Page>
        <DesktopView>
          <ItemDesktopHeaderClient />
          <br />
        </DesktopView>
        <Wrapper>
          <MobileTabletView>
            <ItemMobileHeaderClient title={title} />
          </MobileTabletView>

          <div style={{ display: 'none' }}>
            {productInfo.imagesUrls?.map((image, index) => (
              <img alt={`${title} — фото ${index + 1}`} key={`${image}-${index}`} src={image} />
            ))}
          </div>
          <ItemGalleryClient imagesUrls={productInfo.imagesUrls} title={title} />

          <BlockTitle style={{ marginBottom: '8px', width: '100%' }} topSpace={'sm'}>
            {title}
          </BlockTitle>

          <FlexRowBetween style={{ alignItems: 'center' }}>
            <Paragraph>Залог {productInfo.pledge}₽</Paragraph>
            <SubTitle>
              <ExistedPrice productInfo={productInfo} />
            </SubTitle>
          </FlexRowBetween>

          <BtnWrapper>
            <ItemOrderClient ownerId={productInfo.owner?.id} productInfo={productInfo} />
            <FlexRowBetween style={{ gap: '8px' }}>
              <ButtonColumn
                bottom={
                  <>
                    {productReviews.totalCount || 0}{' '}
                    {displayStringByAmount('отзыв', productReviews.totalCount || 0)}
                  </>
                }
                top={
                  <>
                    <img alt={''} src={'/images/interface/star.svg'} />
                    &nbsp;{avgRating.toFixed(1)}
                  </>
                }
                onClick={() => setOpenRating(true)}
              />
              <ButtonColumn bottom={<>Циклов аренды</>} top={<>{productInfo.rentCyclesCount}</>} />
            </FlexRowBetween>
          </BtnWrapper>

          <TableWrapper>
            <Paragraph style={{ marginBottom: '16px' }}>
              В данной таблице указаны варианты аренды и цены
            </Paragraph>
            <FlexColumnBetween style={{ gap: '8px' }}>
              {[
                ['1 день', productInfo.dayPrice],
                ['3 дня', productInfo.threeDaysPrice],
                ['Неделя', productInfo.weekPrice],
                ['Месяц', productInfo.monthPrice],
              ].map(([label, price]) => (
                <FlexRowBetween key={String(label)} style={{ alignItems: 'center' }}>
                  <Paragraph style={{ color: colors.black }}>{label}</Paragraph>
                  <Paragraph style={{ color: '#008C8C', fontWeight: 600 }}>{price} ₽</Paragraph>
                </FlexRowBetween>
              ))}
            </FlexColumnBetween>
          </TableWrapper>
          <br />

          {!!productInfo.defectFines?.length && (
            <TableWrapper>
              <Paragraph style={{ marginBottom: '16px' }}>
                В данной таблице указаны штрафы за дефекты
              </Paragraph>
              <FlexColumnBetween style={{ gap: '8px' }}>
                {productInfo.defectFines.map((defect, index) => (
                  <FlexRowBetween key={index} style={{ alignItems: 'center' }}>
                    <Paragraph style={{ color: colors.black, maxWidth: '85%' }}>
                      {defect.defectName}
                    </Paragraph>
                    <Paragraph
                      style={{
                        color: colors.primary,
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        width: 'max-content',
                      }}
                    >
                      {defect.amount} ₽
                    </Paragraph>
                  </FlexRowBetween>
                ))}
              </FlexColumnBetween>
            </TableWrapper>
          )}

          <BlockTitle style={{ marginBottom: '8px', width: '100%' }} topSpace={'xl'}>
            О товаре
          </BlockTitle>
          <Paragraph style={{ width: '100%' }}>
            <LineBreaks>{productInfo.description || ''}</LineBreaks>
          </Paragraph>

          <ProductCharacteristics product={productInfo} />

          <BlockTitle style={{ marginBottom: '8px', width: '100%' }} topSpace={'xl'}>
            Доставка
          </BlockTitle>
          <div style={{ alignSelf: 'flex-start' }}>
            {productInfo.deliveryType === 'countryWithSelection' && (
              <FlexColumnStart>
                <Paragraph>В городах: </Paragraph>
                <MediumTextL>{productInfo.deliveryCities?.join(', ')}</MediumTextL>
              </FlexColumnStart>
            )}
            {productInfo.deliveryType === 'russiaAny' && <Paragraph>В пределах РФ</Paragraph>}
            {productInfo.deliveryType === 'notAvailable' && (
              <Paragraph>Варианты доставки не указаны арендодателем</Paragraph>
            )}
          </div>

          <BlockTitle style={{ marginBottom: '8px', width: '100%' }} topSpace={'xl'}>
            Самовывоз
          </BlockTitle>
          {productInfo.pickupAddress ? (
            <LocationWrapper>
              <div>
                <LocationSubTitle>{productInfo.pickupAddress.city}</LocationSubTitle>
                <Paragraph style={{ lineHeight: '23.36px' }}>
                  {productInfo.pickupAddress.address}
                </Paragraph>
                {productInfo.pickupAddress.postcode && (
                  <Paragraph style={{ lineHeight: '23.36px' }}>
                    Почтовый индекс: {productInfo.pickupAddress.postcode}
                  </Paragraph>
                )}
              </div>
              <ItemLocationClient pickupAddress={productInfo.pickupAddress} />
            </LocationWrapper>
          ) : (
            <Paragraph style={{ alignSelf: 'flex-start' }}>
              Адрес самовывоза не указан арендодателем
            </Paragraph>
          )}

          <OwnerLink href={`/users/${productInfo.owner?.id}`}>
            <BlockTitle style={{ marginBottom: '8px', width: '100%' }} topSpace={'xl'}>
              {productInfo.owner?.fullName}
            </BlockTitle>
            <Box style={{ gap: '4px', margin: '8px 0 16px' }}>
              <img alt={'star'} src={'/images/interface/star.svg'} />
              <Paragraph>
                <span style={{ color: '#000' }}>{avgSellerRating.toFixed(1)} </span>·
              </Paragraph>
            </Box>
          </OwnerLink>

          <FlexColumnStart style={{ marginTop: '32px', width: '100%' }}>
            <FlexRowBetween style={{ alignItems: 'center', margin: '0 0 8px', width: '100%' }}>
              <BlockTitle style={{ width: '100%' }}>Отзывы </BlockTitle>
              <ReviewsLinkButton
                aria-label="Открыть все отзывы"
                onClick={() => setOpenRating(true)}
                type="button"
              >
                <img alt={''} src={'/images/buttons/arrow-right-black.svg'} />
              </ReviewsLinkButton>
            </FlexRowBetween>
            <FlexRow style={{ gap: '4px', justifyContent: 'start' }}>
              <img alt={'star'} src={'/images/interface/star.svg'} />
              <Paragraph>
                <span style={{ color: '#000' }}>{(productInfo.rating || 0).toFixed(1)} </span>·{' '}
                {productReviews.totalCount || 0}{' '}
                {displayRateByAmount('оцен', productReviews.totalCount || 0)} на товар
              </Paragraph>
            </FlexRow>
            <br />
            <ReviewsWrapper>
              {reviews.map(review => (
                <ReviewItem
                  avatar={review.buyerAvatarImageUrl || ''}
                  dateAgo={getDateAgo(review.createOn)}
                  desc={review.content}
                  key={review.reviewId}
                  name={review.buyerUserName}
                  rating={review.rating}
                />
              ))}
            </ReviewsWrapper>
          </FlexColumnStart>

          <BottomButton onClick={() => setOpenRating(true)} variant={'secondary'} fullwidth>
            Все отзывы
          </BottomButton>
        </Wrapper>
      </Page>
      <ItemBottomMenuClient />
    </>
  )
}
